import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import CoralogixAddNew from "./images/Coralogix_add_new.png";
import CoralogixContextualDataGenericWebhook from "./images/Coralogix_contextual_data_generic_webhook.png";
import CoralogixCopyWebhookURL from "./images/Coralogix_copy_url.png";
import CoralogixGenerateURL from "./images/Coralogix_generate_url.png";
import CoralogixIntegrations from "./images/Coralogix_integrations_buttons.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import CoralogixConnectForm from "./CoralogixConnectForm";

const INTEGRATION_CODE_NAME = "coralogix";

const CoralogixIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCoralogixIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCoralogixIntegration(values.webhookURL.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integrated successfully`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} webhook.`
        }
        image={logos.CoralogixLogo}
        iconBorderRadius={0}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <WebhookConfig
              toolName={integration.codeName}
              toolDisplayName={integration.displayName}
            />
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Navigate to Coralogix integrations">
                  <Typography variant="body1">
                    Open your Coralogix account and sign in.
                  </Typography>
                  <Box style={{ display: "flex", marginTop: "10px" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://dashboard.app.eu2.coralogix.com/#/login/user",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none" }}
                    >
                      Sign in to Coralogix
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create new webhook URL">
                  <Typography variant="body1">
                    1. Click on <strong>Integrations</strong> →{" "}
                    <strong>Integrations</strong> in the sidebar
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: CoralogixIntegrations,
                        altext: "Click on Integrations and Integrations",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body1">
                    2. Click on <strong>Contextual Data</strong> tab then click
                    on <strong>Add</strong> in the Generic Webhook card
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: CoralogixContextualDataGenericWebhook,
                        altext:
                          "Click on Contextual Data tab then on the Add button of Generic Webhook ",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body1">
                    3. Click <strong>Add New</strong> to create a new webhook
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: CoralogixAddNew,
                        altext: "Click on the Add New button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body1">
                    4. Generate the webhook then click Generate URL
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: CoralogixGenerateURL,
                        altext: "Generate your webhook then click Generate URL",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body1">
                    5. Copy the <strong>Webhook URL</strong> and paste it in the
                    input field below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: CoralogixCopyWebhookURL,
                        altext: "Copy the Webhook URL",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep id="connectCoralogix" title="Connect Coralogix">
                  <Box width={700}>
                    <CoralogixConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CoralogixIntegration;
